/**
 * AssetConstituents collection
 * We now get a return with objects - the key representing the constituent type.
 * There is currently no individual call to get the constituents of a type for an asset.
 * This collection makes the fetch and splits off the return into the appropriate constituent sub collection.
 * Each collection may need a different comparator and definitely needs its own model type (looking at you clips)
 * The alternative is that we get in a mess of declaring all these collections within a controller and then have a
 * specific call for each - why not just fetch here and get it all for free?
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/assetConstituent';

import Clips from './assetConstituents_clips';
import Keyframes from './assetConstituents_keyframes';
import Layers from './assetConstituents_layers';
import Pages from './assetConstituents_pages';

import {constituents as constituentProps} from '../properties/asset';

const ConsituentCollections = {
	clips: Clips,
	keyframes: Keyframes,
	layers: Layers,
	pages: Pages,
};

export default class AssetConstituents extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object}  methods       - Collection RPC methods
	 * @property {method}  methods.read  - Fetch constituents
	 */
	get methods() {
		return {
			read: ['getConstituentsForAsset', 'assetId'],
		};
	}

	/**
	 * @property {object}   parsers       - Collection parsers
	 * @property {function} parsers.read  - Parse the results of fetch
	 */
	get parsers() {
		return _.extend(super.parsers, {
			/**
			 * @function
			 * @param {array} data Data
			 * @param {object} opts Request options
			 */
			read(data, opts) {
				// Where k could be
				// clips
				// keyframes
				// layers
				// pages
				_.each(data, (v, k) => {
					const subCollection = this[k];

					// Set models for sub colelction
					subCollection.set(v);

					// Check for selected constituent and store it
					const selected = subCollection.findWhere({display: 1});
					if (selected) {
						this.displayed = selected;
					}

					delete data[k];
				});

				return data;
			},
		});
	}

	/**
	 * @override
	 * @param {array}  models  An array of models or model attirbutes
	 * @param {object} options Options
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * AssetId that the constituents reference
		 * @type {Number}
		 * @default
		 */
		this.assetId = opts.assetId || 0;

		this.asset = opts.asset || null;

		/**
		 * The currently selected constituent for display
		 * @memberOf Netx.Collections.AssetConstituents
		 * @alias displayed
		 * @type {module:asset/models/assetConstituent}
		 */
		this.displayed = null;

		/**
		 * @memberOf Netx.Collections.AssetConstituents
		 * @alias properties
		 * @type {object}
		 */
		this.properties = opts.properties || constituentProps;

		// Our clip types
		// this.clips     = new Netx.Collections.AssetConstituents_clips
		// this.keyframes = new Netx.Collections.AssetConstituents_keyframes
		// this.layers    = new Netx.Collections.AssetConstituents_layers
		// this.pages     = new Netx.Collections.AssetConstituents_pages
		_.each(this.properties.types, (thing, id) => {
			const things = new ConsituentCollections[`${thing}s`]([], {asset: this.asset});

			things
				// The subcollections should mirror the read events of the parent collections, as they're all fetched as one:
				.listenTo(this, 'request:read', (collection, xhr, opts) => {
					if (collection !== this) return;
					!opts.silent && things.trigger('request:read', things, xhr, opts);
				})
				// When it syncs we sync
				.listenTo(this, 'sync', (collection, resp, opts) => {
					if (collection !== this) return;
					!opts.silent && things.trigger('sync', things, resp, opts);
				});

			this.listenTo(things, 'sync', (collection) => {
				if (collection !== things) return;
				const displayed = this.findWhere({display: 1});
				if (displayed) {
					this.displayed = displayed;
				}
			}).listenTo(things, 'change:display', (model, display) => {
				if (display && this.displayed !== model) {
					this.displayed && this.displayed.set('display', 0);
					this.displayed = model;
				}
			});

			this[thing + 's'] = things;
			this.addSubCollection(things);
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'AssetConstituents';
	}
}
